import type { ProblemDetails } from "./types";
import { safeReturnPath } from "./safe-path";
import { humanizeField, normalizeErrors } from "./form-errors";

/**
 * Typed error thrown by the fetch wrapper. Parses RFC 7807 problem+json so
 * callers can surface field-level validation (`errors`) and human messages
 * (`detail` / `title`) in toasts.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly title?: string;
  readonly detail?: string;
  readonly code?: string;
  readonly errors?: Record<string, string[]>;
  readonly traceId?: string;

  constructor(status: number, problem?: ProblemDetails, fallbackMessage?: string) {
    const normalized = normalizeErrors(problem?.errors);
    const validationMessage = Object.entries(normalized.byField)
      .map(([key, messages]) =>
        `${humanizeField(normalized.labels[key] ?? key)}: ${messages[0]}`,
      )
      .join(" ");
    const message =
      problem?.detail ||
      validationMessage ||
      problem?.title ||
      fallbackMessage ||
      `Request failed with status ${status}`;
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.title = problem?.title;
    this.detail = problem?.detail;
    this.code = problem?.code;
    this.errors = problem?.errors;
    this.traceId = problem?.traceId;
  }

  /** Convenience: first message for a given field (case-insensitive). */
  fieldError(field: string): string | undefined {
    if (!this.errors) return undefined;
    const key = Object.keys(this.errors).find(
      (k) => k.toLowerCase() === field.toLowerCase(),
    );
    return key ? this.errors[key]?.[0] : undefined;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isRateLimited(): boolean {
    return this.status === 429;
  }
}

export interface ApiOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  /** When true (default), a JSON body is stringified and content-type set. */
  json?: boolean;
  /** Skip the automatic 401 -> /signin redirect (e.g. for auth probes). */
  suppressAuthRedirect?: boolean;
}

async function parseProblem(res: Response): Promise<ProblemDetails | undefined> {
  const contentType = res.headers.get("content-type") ?? "";
  if (
    contentType.includes("application/problem+json") ||
    contentType.includes("application/json")
  ) {
    try {
      return (await res.json()) as ProblemDetails;
    } catch {
      return undefined;
    }
  }
  return undefined;
}

/**
 * Single fetch wrapper for the whole app. Always relative (`/api/...`) so the
 * Next rewrite keeps the session cookie first-party. Adds `credentials: include`,
 * disables caching for authed reads, and normalises errors to {@link ApiError}.
 */
export async function apiFetch<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const {
    body,
    json = true,
    headers,
    suppressAuthRedirect,
    ...rest
  } = options;

  const finalHeaders = new Headers(headers);
  let finalBody: BodyInit | undefined;

  if (body instanceof FormData) {
    finalBody = body;
  } else if (body !== undefined && json) {
    finalHeaders.set("Content-Type", "application/json");
    finalBody = JSON.stringify(body);
  } else if (body !== undefined) {
    finalBody = body as BodyInit;
  }

  if (!finalHeaders.has("Accept")) {
    finalHeaders.set("Accept", "application/json");
  }

  let res: Response;
  try {
    res = await fetch(path, {
      ...rest,
      headers: finalHeaders,
      body: finalBody,
      credentials: "include",
      cache: "no-store",
    });
  } catch {
    throw new ApiError(0, undefined, "Unable to reach the server. Check your connection.");
  }

  if (res.status === 401 && !suppressAuthRedirect) {
    if (typeof window !== "undefined") {
      const returnUrl = encodeURIComponent(
        safeReturnPath(window.location.pathname + window.location.search),
      );
      window.location.assign(`/signin?returnUrl=${returnUrl}`);
    }
    throw new ApiError(401, await parseProblem(res), "Please sign in to continue.");
  }

  if (!res.ok) {
    throw new ApiError(res.status, await parseProblem(res));
  }

  if (res.status === 204) {
    return undefined as T;
  }

  const contentType = res.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    return (await res.json()) as T;
  }

  // Callers expecting text/blob handle those directly via apiFetchRaw.
  return (await res.text()) as unknown as T;
}

/** Raw response variant for binary downloads (e.g. badge SVGs). */
export async function apiFetchRaw(path: string, options: RequestInit = {}): Promise<Response> {
  const res = await fetch(path, {
    ...options,
    credentials: "include",
    cache: "no-store",
  });
  if (!res.ok) {
    throw new ApiError(res.status, await parseProblem(res));
  }
  return res;
}
